import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { DailyWinsProvider } from './context/DailyWinsContext';
import { PatientReportBatchNotifyProvider } from './context/PatientReportBatchNotifyContext';
import LoginPage from './components/Login/LoginPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import ReportsLayout from './components/reports/ReportsLayout';
import SuperuserRoute from './components/SuperuserRoute';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import ServiceWorkerUpdateBanner from './components/ServiceWorkerUpdateBanner';
import './App.css';

function lazyRetry(importFn) {
  return React.lazy(() =>
    importFn().catch(() =>
      new Promise((resolve) => setTimeout(resolve, 1500)).then(() => importFn()),
    ),
  );
}

const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const CareQueue = lazyRetry(() => import('./pages/CareQueue'));
const AdminDashboard = lazyRetry(() => import('./pages/AdminDashboard'));
const MyBonus = lazyRetry(() => import('./pages/MyBonus'));
const BonusSettings = lazyRetry(() => import('./pages/BonusSettings'));
const KpiReport = lazyRetry(() => import('./pages/KpiReport'));
const ProSummaryReport = lazyRetry(() => import('./pages/ProSummaryReport'));
const ProDetailReport = lazyRetry(() => import('./pages/ProDetailReport'));
const Patients = lazyRetry(() => import('./pages/Patients'));
const Worklist = lazyRetry(() => import('./pages/Worklist'));
const UserManagement = lazyRetry(() => import('./pages/UserManagement'));
const PatientAssignments = lazyRetry(() => import('./pages/PatientAssignments'));
const ReasonsAdmin = lazyRetry(() => import('./pages/ReasonsAdmin'));
const ResolveNotes = lazyRetry(() => import('./pages/ResolveNotes'));
const CareAlertSettings = lazyRetry(() => import('./pages/CareAlertSettings'));
const TenantPhoneNumbers = lazyRetry(() => import('./pages/TenantPhoneNumbers'));
const PathwayBuilder = lazyRetry(() => import('./pages/PathwayBuilder'));
const PatientDetail = lazyRetry(() => import('./pages/PatientDetail'));
const Insurances = lazyRetry(() => import('./pages/Insurances'));
const EducationLibrary = lazyRetry(() => import('./pages/EducationLibrary'));
const Clients = lazyRetry(() => import('./pages/Clients'));
const ClientSites = lazyRetry(() => import('./pages/ClientSites'));
const CptCodes = lazyRetry(() => import('./pages/CptCodes'));
const Providers = lazyRetry(() => import('./pages/Providers'));
const ServiceTypes = lazyRetry(() => import('./pages/ServiceTypes'));
const ReimbursementMethods = lazyRetry(() => import('./pages/ReimbursementMethods'));
const DiagnosisCodes = lazyRetry(() => import('./pages/DiagnosisCodes'));
const SessionMinutesDefaults = lazyRetry(() => import('./pages/SessionMinutesDefaults'));
const BillingRules = lazyRetry(() => import('./pages/BillingRules'));
const SuperuserTenants = lazyRetry(() => import('./pages/SuperuserTenants'));
const SuperuserResetConsent = lazyRetry(() => import('./pages/SuperuserResetConsent'));
const SuperuserRateSchedules = lazyRetry(() => import('./pages/SuperuserRateSchedules'));
const DeviceSetup = lazyRetry(() => import('./pages/DeviceSetup'));
const PatientDevices = lazyRetry(() => import('./pages/PatientDevices'));
const DeviceDiscovery = lazyRetry(() => import('./pages/DeviceDiscovery'));
const DeviceWarRoom = lazyRetry(() => import('./pages/DeviceWarRoom'));
const RpmDeviceHelpQueue = lazyRetry(() => import('./pages/RpmDeviceHelpQueue'));
const UnsyncedReadings = lazyRetry(() => import('./pages/UnsyncedReadings'));
const RpmChartSettings = lazyRetry(() => import('./pages/RpmChartSettings'));
const SnBackfilling = lazyRetry(() => import('./pages/SnBackfilling'));
const UnassignedDeviceAlerts = lazyRetry(() => import('./pages/UnassignedDeviceAlerts'));
const ParameterMismatchAlerts = lazyRetry(() => import('./pages/ParameterMismatchAlerts'));
const ReadingQuarantineAlerts = lazyRetry(() => import('./pages/ReadingQuarantineAlerts'));
const ReportsIndex = lazyRetry(() => import('./pages/ReportsIndex'));
const ReportPackage = lazyRetry(() => import('./pages/ReportPackage'));
const MessagesReport = lazyRetry(() => import('./pages/MessagesReport'));
const CareAlertsReport = lazyRetry(() => import('./pages/CareAlertsReport'));
const CareTimeReport = lazyRetry(() => import('./pages/CareTimeReport'));
const CareTimeByPatientReport = lazyRetry(() => import('./pages/CareTimeByPatientReport'));
const ResponseTimeReport = lazyRetry(() => import('./pages/ResponseTimeReport'));
const DiseaseReport = lazyRetry(() => import('./pages/DiseaseReport'));
const PatientSummaryReport = lazyRetry(() => import('./pages/PatientSummaryReport'));
const PatientReportBatch = lazyRetry(() => import('./pages/PatientReportBatch'));
const VitalsReport = lazyRetry(() => import('./pages/VitalsReport'));
const BillingReport = lazyRetry(() => import('./pages/BillingReport'));
const BillingReview = lazyRetry(() => import('./pages/BillingReview'));
const VolumeActivityReport = lazyRetry(() => import('./pages/VolumeActivityReport'));
const PatientListingReport = lazyRetry(() => import('./pages/PatientListingReport'));
const HowReportsWork = lazyRetry(() => import('./pages/HowReportsWork'));
const ReportSettings = lazyRetry(() => import('./pages/ReportSettings'));
const BulkMessaging = lazyRetry(() => import('./pages/BulkMessaging'));
const BulkPathwayAssignment = lazyRetry(() => import('./pages/BulkPathwayAssignment'));
const Helpdesk = lazyRetry(() => import('./pages/Helpdesk'));

function LazyFallback() {
  return <div className="app-loading"><p>Loading…</p></div>;
}

/** Routes under `/superuser/*` use <SuperuserRoute /> — superuser role only (not org admin). */

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <p>Loading…</p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <p>Loading…</p>
      </div>
    );
  }
  if (user) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="care-queue" element={<CareQueue />} />
        <Route path="admin-dashboard" element={<AdminDashboard />} />
        <Route path="my-bonus" element={<MyBonus />} />
        <Route path="admin/bonus-settings" element={<BonusSettings />} />
        <Route path="patients" element={<Patients />} />
        <Route path="patients/:patientId" element={<PatientDetail />} />
        <Route path="worklist" element={<Worklist />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="patient-assignments" element={<PatientAssignments />} />
        <Route path="admin/reasons" element={<ReasonsAdmin />} />
        {/* Legacy reason-page routes, consolidated into /admin/reasons. */}
        <Route path="admin/deactivation-reasons" element={<Navigate to="/admin/reasons" replace />} />
        <Route path="admin/program-hold-reasons" element={<Navigate to="/admin/reasons" replace />} />
        <Route path="admin/program-discharge-reasons" element={<Navigate to="/admin/reasons" replace />} />
        <Route path="admin/resolve-notes" element={<ResolveNotes />} />
        <Route path="admin/care-alert-settings" element={<CareAlertSettings />} />
        <Route path="admin/phone-numbers" element={<TenantPhoneNumbers />} />
        <Route path="admin/report-settings" element={<ReportSettings />} />
        <Route path="admin/help-phone" element={<Navigate to="/admin/phone-numbers" replace />} />
        <Route path="pathways" element={<PathwayBuilder />} />
        <Route path="insurances" element={<Insurances />} />
        <Route path="education-library" element={<EducationLibrary />} />
        <Route path="rpm/devices/setup" element={<DeviceSetup />} />
        <Route path="rpm/devices/assignments" element={<PatientDevices />} />
        <Route path="rpm/devices/war-room" element={<DeviceWarRoom />} />
        <Route path="rpm/devices/discovery" element={<Navigate to="/rpm/devices/war-room" replace />} />
        <Route path="rpm/devices/help-queue" element={<RpmDeviceHelpQueue />} />
        <Route path="rpm/unsynced-readings" element={<UnsyncedReadings />} />
        <Route path="rpm/devices/reading-charts" element={<RpmChartSettings />} />
        <Route path="rpm/devices/sn-backfilling" element={<SnBackfilling />} />
        <Route path="alerts/unassigned-devices" element={<UnassignedDeviceAlerts />} />
        <Route path="alerts/parameter-mismatch" element={<ParameterMismatchAlerts />} />
        <Route path="alerts/reading-quarantine" element={<ReadingQuarantineAlerts />} />
        <Route path="reports" element={<ReportsLayout />}>
          <Route index element={<ReportsIndex />} />
          <Route path="package" element={<ReportPackage />} />
          <Route path="messages" element={<MessagesReport />} />
          <Route path="care-alerts" element={<CareAlertsReport />} />
          <Route path="care-time" element={<CareTimeReport />} />
          <Route path="care-time/by-patient" element={<CareTimeByPatientReport />} />
          <Route path="response-time" element={<ResponseTimeReport />} />
          <Route path="disease" element={<DiseaseReport />} />
          <Route path="patient-summary" element={<PatientSummaryReport />} />
          <Route path="patient-batch" element={<PatientReportBatch />} />
          <Route path="vitals" element={<VitalsReport />} />
          <Route path="billing" element={<BillingReport />} />
          <Route path="billing-review" element={<BillingReview />} />
          <Route path="volume-activity" element={<VolumeActivityReport variant="detail" />} />
          <Route path="volume-activity-summary" element={<VolumeActivityReport variant="summary" />} />
          <Route path="patient-listing" element={<PatientListingReport />} />
          <Route path="kpi" element={<KpiReport />} />
          <Route path="pro-summary" element={<ProSummaryReport />} />
          <Route path="pro-detail" element={<ProDetailReport />} />
          <Route path="how-reports-work" element={<HowReportsWork />} />
        </Route>
        <Route path="bulk-messaging" element={<BulkMessaging />} />
        <Route path="bulk-pathways" element={<BulkPathwayAssignment />} />
        <Route path="helpdesk" element={<Helpdesk />} />
        <Route path="billing-setup/clients" element={<Clients />} />
        <Route path="billing-setup/client-sites" element={<ClientSites />} />
        <Route path="billing-setup/cpt-codes" element={<CptCodes />} />
        <Route path="billing-setup/providers" element={<Providers />} />
        <Route path="billing-setup/service-types" element={<ServiceTypes />} />
        <Route path="billing-setup/reimbursement-methods" element={<ReimbursementMethods />} />
        <Route path="billing-setup/diagnosis-codes" element={<DiagnosisCodes />} />
        <Route path="billing-setup/session-minutes-defaults" element={<SessionMinutesDefaults />} />
        <Route path="billing-setup/billing-rules" element={<BillingRules />} />
        <Route path="superuser" element={<SuperuserRoute />}>
          <Route path="tenants" element={<SuperuserTenants />} />
          <Route path="reset-consent" element={<SuperuserResetConsent />} />
          <Route path="rate-schedules" element={<SuperuserRateSchedules />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <DailyWinsProvider>
            <PatientReportBatchNotifyProvider>
              <AppRoutes />
              <ServiceWorkerUpdateBanner />
            </PatientReportBatchNotifyProvider>
          </DailyWinsProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
